import { createFileRoute } from '@tanstack/react-router'
import {
  LogisticsLayout,
  useOpenDetailTab,
  useOpenLogisticsList,
} from '~/components/routes/logistics/logistics-layout'
import { LogisticsRouteGuard } from '~/components/routes/logistics/logistics-route-guard'
import { OperationsTourDetailMap } from '~/components/routes/logistics/operations-tour-detail-map'
import { OperationsTourDetailPanel } from '~/components/routes/logistics/operations-tour-detail-panel'
import {
  DETAIL_PANEL_WIDTH_PX,
  LIST_PANEL_LEFT_PX,
} from '~/components/routes/logistics/simulator-layout'
import { LOGISTICS_SIMULATION_READ, LOGISTICS_TOUR_READ } from '~/components/routes/logistics/tabs'
import { useActiveTourDetailQuery } from '~/lib/logistics-operations-query'
import { getTuyauErrorStatus } from '~/lib/utils'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import {
  type LogisticsSearch,
  type OperationsDetailTab,
  operationsTourSearch,
} from '../-search-schema'

export const Route = createFileRoute('/_authenticated/logistics/operations/$tourId')({
  staticData: {
    permission: LOGISTICS_TOUR_READ,
  },
  component: OperationsTourDetailRoute,
  // Mêmes paramètres que la liste : ses filtres suivent l'utilisateur dans le détail et au retour.
  // L'onglet du panneau en plus, comme le détail d'une simulation (LU-181).
  validateSearch: operationsTourSearch,
})
withRoutePermissions(Route)

/** Mêmes gardes que la liste (`logistics/index.tsx`) : flag `logistique` et droit v2. */
function OperationsTourDetailRoute() {
  const { tourId } = Route.useParams()
  // L'onglet du panneau reste propre au détail : la liste ne le reçoit pas.
  const { detailTab = 'suivi', ...search } = Route.useSearch()
  // La barre du détail mène à la liste : un onglet ou un filtre choisi ici s'y applique.
  const openList = useOpenLogisticsList(search)
  const openDetailTab = useOpenDetailTab<OperationsDetailTab>('suivi')

  return (
    <LogisticsRouteGuard permission={LOGISTICS_TOUR_READ}>
      <OperationsTourDetail
        id={Number(tourId)}
        search={search}
        onSearchChange={openList}
        detailTab={detailTab}
        onDetailTabChange={openDetailTab}
      />
    </LogisticsRouteGuard>
  )
}

interface OperationsTourDetailProps {
  id: number
  search: LogisticsSearch
  onSearchChange: (patch: Partial<LogisticsSearch>) => void
  detailTab: OperationsDetailTab
  onDetailTabChange: (tab: OperationsDetailTab) => void
}

/**
 * Détail d'une tournée en vigueur (#891) : son panneau sur la carte de la tournée (son tracé, ses
 * lieux numérotés), comme le détail d'une simulation.
 */
function OperationsTourDetail({
  id,
  search,
  onSearchChange,
  detailTab,
  onDetailTabChange,
}: OperationsTourDetailProps) {
  const detail = useActiveTourDetailQuery(id)
  const canReadSimulations = usePermissionGuard(LOGISTICS_SIMULATION_READ).authorized
  // Sortie de la liste (fin de service, remplacement) : la relecture répond 404, et l'ancien
  // déroulé ne reste pas affiché sous le message d'erreur.
  const tour = getTuyauErrorStatus(detail.error) === 404 ? undefined : detail.data

  return (
    <LogisticsLayout
      search={search}
      onSearchChange={onSearchChange}
      containerClassName="relative overflow-hidden p-0"
    >
      <OperationsTourDetailMap detail={tour} search={search} storeCards={canReadSimulations} />

      <div
        className="absolute inset-y-4 z-10"
        style={{ left: LIST_PANEL_LEFT_PX, width: DETAIL_PANEL_WIDTH_PX }}
      >
        <OperationsTourDetailPanel
          tourId={id}
          detail={tour}
          isLoading={detail.isLoading}
          error={detail.error}
          tab={detailTab}
          onTabChange={onDetailTabChange}
        />
      </div>
    </LogisticsLayout>
  )
}
